import {
  KANBAN_PRIORITY,
  PRIORITY_COLORS,
  type KanbanPriority,
} from "./kanban-constants";
import type {
  KanbanCardMetadata,
  KanbanColumn,
  ParsedKanban,
} from "./parse-mermaid-kanban";

export interface MetadataEntry {
  key: string;
  value: string;
}

export interface BadgeMetadataEntry extends MetadataEntry {
  key: BadgeKey;
}

export enum BadgeKey {
  Assigned = "assigned",
  Ticket = "ticket",
}

export const BADGE_LABEL: Record<BadgeKey, string> = {
  [BadgeKey.Assigned]: "Assigned",
  [BadgeKey.Ticket]: "Ticket",
};

const PRIORITY_VALUES = Object.values(KANBAN_PRIORITY) as KanbanPriority[];

function isBadgeKey(value: string): value is BadgeKey {
  return value === BadgeKey.Assigned || value === BadgeKey.Ticket;
}

function isKanbanPriority(value: string): value is KanbanPriority {
  return PRIORITY_VALUES.includes(value as KanbanPriority);
}

export function isBadgeMetadataEntry(
  entry: MetadataEntry,
): entry is BadgeMetadataEntry {
  return isBadgeKey(entry.key);
}

export function toCardKey(input: { columnId: string; cardId: string }): string {
  return `${input.columnId}:${input.cardId}`;
}

export function toTitleCase(rawKey: string): string {
  if (rawKey.length === 0) {
    return rawKey;
  }

  return rawKey
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

export function getMetadataEntries(
  metadata: KanbanCardMetadata,
): MetadataEntry[] {
  return Object.entries(metadata)
    .filter((entry): entry is [string, string] => {
      const value = entry[1];
      return typeof value === "string" && value.trim().length > 0;
    })
    .map(([key, value]) => ({
      key,
      value,
    }));
}

export function getPriorityAccentColor(priority?: string): string | null {
  if (!priority || !isKanbanPriority(priority)) {
    return null;
  }

  if (
    priority !== KANBAN_PRIORITY.High &&
    priority !== KANBAN_PRIORITY.VeryHigh
  ) {
    return null;
  }

  return PRIORITY_COLORS[priority];
}

export function getKanbanCardKeys(parsed: ParsedKanban | null): Set<string> {
  if (!parsed) {
    return new Set<string>();
  }

  return parsed.columns.reduce((cardKeys, column) => {
    column.cards.forEach((card) => {
      cardKeys.add(
        toCardKey({
          columnId: column.id,
          cardId: card.id,
        }),
      );
    });

    return cardKeys;
  }, new Set<string>());
}

export function getTotalCards(parsed: ParsedKanban | null): number {
  if (!parsed) {
    return 0;
  }

  return parsed.columns.reduce(
    (cardCount, column) => cardCount + column.cards.length,
    0,
  );
}

export function getCompactPreviewColumns(parsed: ParsedKanban): KanbanColumn[] {
  return parsed.columns.slice(0, 5);
}
